import { ref } from 'vue';
import Dexie from 'dexie';
import { db, ContratLocal, PaiementLocal, UsagerLocal, EmplacementLocal, SyncQueue } from '@/database/db';
import ApiService from './ApiService';

// ============================================
// Service de synchronisation Offline-First
// ============================================

class SyncService {
  private isOnline = ref(navigator.onLine);
  private isOfflineMode = ref(this.loadOfflineModeFromStorage()); // Mode offline forcé par l'utilisateur (persisté)
  private syncInterval: number | null = null;
  private syncInProgress = ref(false);
  private onlineListeners: Array<() => void> = [];
  private offlineListeners: Array<() => void> = [];

  constructor() {
    this.setupNetworkListeners();
  }

  /**
   * Charge l'état du mode offline depuis localStorage
   */
  private loadOfflineModeFromStorage(): boolean {
    try {
      const saved = localStorage.getItem('offlineMode');
      return saved === 'true';
    } catch (error) {
      console.warn('Impossible de charger l\'état du mode offline:', error);
      return false;
    }
  }

  /**
   * Sauvegarde l'état du mode offline dans localStorage
   */
  private saveOfflineModeToStorage(enabled: boolean): void {
    try {
      localStorage.setItem('offlineMode', enabled ? 'true' : 'false');
    } catch (error) {
      console.warn('Impossible de sauvegarder l\'état du mode offline:', error);
    }
  }

  // ============================================
  // Gestion de l'état de connexion
  // ============================================

  /**
   * Vérifie si l'application est en mode offline
   */
  isOffline(): boolean {
    return this.isOfflineMode.value || !this.isOnline.value;
  }

  /**
   * Active/désactive le mode offline-first
   */
  setOfflineMode(enabled: boolean): void {
    this.isOfflineMode.value = enabled;
    this.saveOfflineModeToStorage(enabled);
    this.notifyListeners();
  }

  /**
   * Retourne l'état du mode offline
   */
  getOfflineMode(): boolean {
    return this.isOfflineMode.value;
  }

  /**
   * Vérifie si la synchronisation est en cours
   */
  isSyncing(): boolean {
    return this.syncInProgress.value;
  }

  /**
   * Configure les listeners pour les événements réseau
   */
  private setupNetworkListeners(): void {
    // Restaurer l'état du mode offline au démarrage
    const savedMode = this.loadOfflineModeFromStorage();
    if (savedMode) {
      this.isOfflineMode.value = true;
      console.log('📴 Mode offline restauré depuis localStorage');
    }

    window.addEventListener('online', () => {
      this.isOnline.value = true;
      console.log('🌐 Connexion rétablie');
      this.notifyListeners();
      // Ne synchroniser que si on n'est pas en mode offline forcé
      if (!this.isOfflineMode.value) {
        this.syncAll();
      } else {
        console.log('📴 Mode offline activé: synchronisation ignorée');
      }
    });

    window.addEventListener('offline', () => {
      this.isOnline.value = false;
      console.log('📴 Connexion perdue');
      this.notifyListeners();
    });
  }

  /**
   * Ajoute un listener pour les changements d'état réseau
   */
  onNetworkChange(callback: (isOnline: boolean) => void): () => void {
    const listener = () => callback(this.isOnline.value && !this.isOfflineMode.value);
    this.onlineListeners.push(listener);
    return () => {
      const index = this.onlineListeners.indexOf(listener);
      if (index > -1) this.onlineListeners.splice(index, 1);
    };
  }

  private notifyListeners(): void {
    const isOnline = this.isOnline.value && !this.isOfflineMode.value;
    this.onlineListeners.forEach(listener => listener());
  }

  // ============================================
  // Opérations CRUD avec fallback IndexedDB
  // ============================================

  /**
   * Récupère les données depuis l'API ou IndexedDB
   */
  async fetch<T>(
    endpoint: string,
    localTable: Dexie.Table<T, number>,
    transform?: (data: any) => T
  ): Promise<T[]> {
    if (this.isOffline()) {
      console.log('📦 Mode offline: récupération depuis IndexedDB');
      return await localTable.where('syncStatus').notEqual('deleted').toArray() as T[];
    }

    try {
      const response = await ApiService.get(endpoint);
      if (response.data && response.data.success) {
        const data = response.data.data?.data || response.data.data || [];
        const transformed = transform ? data.map(transform) : data;
        
        // Sauvegarder dans IndexedDB
        await this.saveToLocal(localTable, transformed);
        return transformed;
      }
      throw new Error('Réponse API invalide');
    } catch (error: any) {
      console.warn('⚠️ Erreur API, fallback vers IndexedDB:', error);
      return await localTable.where('syncStatus').notEqual('deleted').toArray() as T[];
    }
  }

  /**
   * Crée une entité (API ou IndexedDB)
   */
  async create<T extends { localId?: string; syncStatus: string; createdAt?: Date; updatedAt?: Date; version?: number }>(
    endpoint: string,
    localTable: Dexie.Table<T, number>,
    data: Omit<T, 'id' | 'localId' | 'syncStatus' | 'createdAt' | 'updatedAt' | 'version'>,
    transform?: (data: any) => T
  ): Promise<T> {
    const localId = db.generateLocalId();
    const now = new Date();
    
    const localEntity = {
      ...data,
      localId,
      syncStatus: this.isOffline() ? 'pending' : 'synced',
      createdAt: now,
      updatedAt: now,
      version: 1
    } as unknown as T;

    // Sauvegarder localement d'abord
    const savedId = await localTable.add(localEntity);
    const saved = await localTable.get(savedId) as T;

    if (this.isOffline()) {
      // Ajouter à la queue de synchronisation
      await this.addToSyncQueue('create', endpoint, saved, localId);
      return saved;
    }

    try {
      // Tenter la synchronisation immédiate
      const response = await ApiService.post(endpoint, data);
      if (response.data && response.data.success) {
        const serverData = transform ? transform(response.data.data) : response.data.data;
        
        // Mettre à jour avec les données du serveur
        const savedWithVersion = saved as any;
        await localTable.update(savedId, {
          ...serverData,
          localId: undefined, // Supprimer le localId une fois synchronisé
          syncStatus: 'synced',
          lastSync: new Date(),
          version: (savedWithVersion.version || 1) + 1
        } as Partial<T>);

        // Retirer de la queue si présent
        await db.syncQueue.where('localId').equals(localId).delete();

        return await localTable.get(savedId) as T;
      }
    } catch (error: any) {
      console.error('Erreur lors de la création:', error);
      await this.addToSyncQueue('create', endpoint, saved, localId);
    }

    return saved;
  }

  /**
   * Met à jour une entité
   */
  async update<T extends { id?: number; localId?: string; syncStatus: string; updatedAt?: Date; version?: number }>(
    endpoint: string,
    localTable: Dexie.Table<T, number>,
    id: number | string,
    data: Partial<T>,
    transform?: (data: any) => T
  ): Promise<T> {
    const entity = await localTable.get(id as number);
    if (!entity) throw new Error('Entité non trouvée');

    const now = new Date();
    const entityWithVersion = entity as any;
    const updated = {
      ...entity,
      ...data,
      syncStatus: this.isOffline() ? 'pending' : 'synced',
      updatedAt: now,
      version: (entityWithVersion.version || 1) + 1
    } as unknown as T;

    await localTable.update(id as number, updated);

    if (this.isOffline()) {
      await this.addToSyncQueue('update', `${endpoint}/${id}`, updated, entity.localId);
      return updated;
    }

    try {
      const response = await ApiService.put(`${endpoint}/${id}`, data);
      if (response.data && response.data.success) {
        const serverData = transform ? transform(response.data.data) : response.data.data;
        const updatedWithVersion = updated as any;
        await localTable.update(id as number, {
          ...serverData,
          syncStatus: 'synced',
          lastSync: now,
          version: (updatedWithVersion.version || 1) + 1
        } as Partial<T>);

        await db.syncQueue.where('entityId').equals(id.toString()).delete();
        return await localTable.get(id as number) as T;
      }
    } catch (error: any) {
      console.error('Erreur lors de la mise à jour:', error);
      await this.addToSyncQueue('update', `${endpoint}/${id}`, updated, entity.localId);
    }

    return updated;
  }

  /**
   * Supprime une entité (soft delete)
   */
  async delete<T extends { id?: number; localId?: string; syncStatus: string; deletedAt?: Date }>(
    endpoint: string,
    localTable: Dexie.Table<T, number>,
    id: number | string
  ): Promise<void> {
    const entity = await localTable.get(id as number);
    if (!entity) throw new Error('Entité non trouvée');

    const now = new Date();
    await localTable.update(id as number, {
      syncStatus: 'pending',
      deletedAt: now
    } as Partial<T>);

    if (this.isOffline()) {
      await this.addToSyncQueue('delete', `${endpoint}/${id}`, entity, entity.localId);
      return;
    }

    try {
      await ApiService.delete(`${endpoint}/${id}`);
      await localTable.delete(id as number);
      await db.syncQueue.where('entityId').equals(id.toString()).delete();
    } catch (error: any) {
      console.error('Erreur lors de la suppression:', error);
      await this.addToSyncQueue('delete', `${endpoint}/${id}`, entity, entity.localId);
    }
  }

  // ============================================
  // Gestion de la queue de synchronisation
  // ============================================

  /**
   * Ajoute une opération à la queue de synchronisation
   */
  private async addToSyncQueue(
    action: 'create' | 'update' | 'delete',
    endpoint: string,
    entity: any,
    localId?: string
  ): Promise<void> {
    await db.syncQueue.add({
      entityType: this.getEntityTypeFromEndpoint(endpoint),
      entityId: entity.id || entity.localId || localId || '',
      localId: localId || entity.localId,
      action,
      data: entity,
      retryCount: 0,
      createdAt: new Date()
    });
  }

  /**
   * Détermine le type d'entité depuis l'endpoint
   */
  private getEntityTypeFromEndpoint(endpoint: string): 'contrat' | 'paiement' | 'usager' | 'emplacement' {
    if (endpoint.includes('contrat')) return 'contrat';
    if (endpoint.includes('paiement')) return 'paiement';
    if (endpoint.includes('usager')) return 'usager';
    if (endpoint.includes('emplacement')) return 'emplacement';
    return 'contrat'; // Par défaut
  }

  /**
   * Synchronise toutes les opérations en attente
   */
  async syncAll(): Promise<{ success: number; failed: number; errors: string[] }> {
    if (this.isOffline() || this.syncInProgress.value) {
      return { success: 0, failed: 0, errors: [] };
    }

    this.syncInProgress.value = true;
    const results = { success: 0, failed: 0, errors: [] as string[] };

    try {
      const queue = await db.syncQueue.orderBy('createdAt').toArray();

      for (const item of queue) {
        try {
          await this.syncQueueItem(item);
          results.success++;
          await db.syncQueue.delete(item.id!);
        } catch (error: any) {
          results.failed++;
          results.errors.push(error.message);
          
          // Incrémenter le compteur de tentatives
          await db.syncQueue.update(item.id!, {
            retryCount: item.retryCount + 1,
            lastAttempt: new Date(),
            error: error.message
          });

          // Si trop de tentatives, marquer comme erreur
          if (item.retryCount >= 5) {
            await db.syncQueue.update(item.id!, {
              error: `Échec après ${item.retryCount} tentatives`
            });
          }
        }
      }
    } finally {
      this.syncInProgress.value = false;
    }

    return results;
  }

  /**
   * Synchronise un élément de la queue
   */
  private async syncQueueItem(item: SyncQueue): Promise<void> {
    const { action, data, entityId, localId } = item;
    const endpoint = this.getEndpointFromEntityType(item.entityType);

    switch (action) {
      case 'create':
        const response = await ApiService.post(endpoint, data);
        if (response.data?.success) {
          // Mettre à jour l'entité locale avec l'ID du serveur
          const table = this.getTableFromEntityType(item.entityType);
          const localEntity = await table.where('localId').equals(localId || '').first();
          if (localEntity) {
            await table.update(localEntity.id!, {
              ...response.data.data,
              localId: undefined,
              syncStatus: 'synced',
              lastSync: new Date()
            });
          }
        }
        break;

      case 'update':
        await ApiService.put(`${endpoint}/${entityId}`, data);
        const updateTable = this.getTableFromEntityType(item.entityType);
        await updateTable.update(entityId as number, {
          syncStatus: 'synced',
          lastSync: new Date()
        });
        break;

      case 'delete':
        await ApiService.delete(`${endpoint}/${entityId}`);
        const deleteTable = this.getTableFromEntityType(item.entityType);
        await deleteTable.delete(entityId as number);
        break;
    }
  }

  /**
   * Obtient l'endpoint API depuis le type d'entité
   */
  private getEndpointFromEntityType(type: string): string {
    const endpoints: Record<string, string> = {
      contrat: '/contrats',
      paiement: '/paiements',
      usager: '/usagers',
      emplacement: '/emplacements'
    };
    return endpoints[type] || '/contrats';
  }

  /**
   * Obtient la table Dexie depuis le type d'entité
   */
  private getTableFromEntityType(type: string): Dexie.Table<any, number> {
    const tables: Record<string, Dexie.Table<any, number>> = {
      contrat: db.contrats,
      paiement: db.paiements,
      usager: db.usagers,
      emplacement: db.emplacements
    };
    return tables[type] || db.contrats;
  }

  /**
   * Sauvegarde les données dans IndexedDB
   */
  private async saveToLocal<T>(table: Dexie.Table<T, number>, data: T[]): Promise<void> {
    if (data.length === 0) return;

    await table.bulkPut(data as any[]);
  }

  /**
   * Démarre la synchronisation automatique périodique
   */
  startAutoSync(intervalMs: number = 30000): void {
    if (this.syncInterval) return;

    this.syncInterval = window.setInterval(() => {
      if (!this.isOffline() && !this.syncInProgress.value) {
        this.syncAll();
      }
    }, intervalMs);
  }

  /**
   * Arrête la synchronisation automatique
   */
  stopAutoSync(): void {
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
      this.syncInterval = null;
    }
  }

  /**
   * Obtient le nombre d'éléments en attente de synchronisation
   */
  async getPendingSyncCount(): Promise<number> {
    return await db.syncQueue.count();
  }

  /**
   * Obtient les statistiques de synchronisation
   */
  async getSyncStats(): Promise<{
    pending: number;
    synced: number;
    errors: number;
    lastSync?: Date;
  }> {
    const pending = await db.syncQueue.count();
    const synced = await db.contrats.where('syncStatus').equals('synced').count() +
                   await db.paiements.where('syncStatus').equals('synced').count() +
                   await db.usagers.where('syncStatus').equals('synced').count() +
                   await db.emplacements.where('syncStatus').equals('synced').count();
    
    const errors = await db.syncQueue.where('retryCount').above(5).count();

    return { pending, synced, errors };
  }
}

// Instance singleton
export const syncService = new SyncService();

